import { MutationCache, QueryClient } from '@tanstack/react-query';
import { ApiError } from './errors';
import { invalidateResource } from './invalidate';

// The variables of an $api mutation, as far as the 409 rule reads them.
type MutationInit = { params?: { path?: Record<string, string> } } | undefined;

// A 409 on a resource no GET reads refetches what its screens read instead (UI architecture §4
// Errors); a read path with parameters is read for the mutation's own path parameters.
const readPaths: Record<string, string[]> = {
  // The verification banner and the onboarding frame each read email_verified.
  '/api/auth/verify-email/resend': ['/api/me', '/api/onboarding'],
  '/api/attributions': ['/api/sales'], // Sales: the row refetches
  '/api/test-chat/sessions': ['/api/test-chat/sessions/{id}/messages'], // that session's thread
  '/api/invitations': ['/api/users'], // the invited users and the seats
  '/admin/api/payments': ['/admin/api/payments/failed'],
};

// Retries (UI architecture §4): queries retry twice with the default backoff, never on a 4xx;
// mutations never retry by themselves.
export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        retry: (failureCount, error) =>
          failureCount < 2 &&
          !(error instanceof ApiError && error.status >= 400 && error.status < 500),
      },
      mutations: { retry: false },
    },
    mutationCache: new MutationCache({
      // A 409 refetches the mutation's resource, then the screen explains (UI architecture §4).
      // TanStack awaits this before the mutation's own onError, so the refetch lands first.
      onError: (error, variables, _onMutateResult, _mutation, { client, mutationKey }) => {
        const [, path] = mutationKey ?? [];
        if (error instanceof ApiError && error.status === 409 && typeof path === 'string') {
          // '/api/conversations/{id}/takeover' → '/api/conversations' and its param 'id'.
          const [resource = path, param] = path.split(/\/\{(\w+)\}/);
          const init = variables as MutationInit;
          const reads = readPaths[resource];
          if (reads !== undefined) {
            return Promise.all(
              reads.map((read) => {
                const readInit = read.includes('{')
                  ? [{ params: { path: init?.params?.path } }]
                  : [];
                return client.invalidateQueries({ queryKey: ['get', read, ...readInit] });
              }),
            );
          }
          if (param === undefined) {
            // The path itself, and its parent for an action under the resource it changes:
            // '/api/billing/change-interval' → '/api/billing'.
            const parent = path.slice(0, path.lastIndexOf('/'));
            return Promise.all([
              invalidateResource(client, path),
              invalidateResource(client, parent),
            ]);
          }
          const id = init?.params?.path?.[param];
          return invalidateResource(client, resource, id);
        }
      },
    }),
  });
}
